import { Image } from '@unistocks/components/basic/image';
import { InputRef } from '@unistocks/components/basic/input';
import { Form } from '@unistocks/components/form/form';
import { MultiStepForm } from '@unistocks/components/form/multi-step-form';
import {
  applyRefinements,
  conditionalRequired,
  createMoreThanSchema,
  createNumberSchema,
  requiredSchema
} from '@unistocks/components/form/validators';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import { TypeaheadRef } from '@unistocks/components/typeahead/typeahead';
import { AssetTypeSelect } from '@unistocks/features/assets/components/asset-type-select';
import { useAssetSearch } from '@unistocks/features/assets/hooks/use-asset-search';
import { FlagKey, FLAGS } from '@unistocks/features/portfolios/flags';
import { PortfolioBase } from '@unistocks/features/portfolios/models/portfolio-base';
import { isPortfolio as isPortfolioCheck } from '@unistocks/features/portfolios/portfolio-utils';
import { isWatchlist as isWatchlistCheck } from '@unistocks/features/watchlists/watchlist-utils';
import { t } from '@unistocks/localization';
import {
  AssetType,
  ExternalAssetSearchDto,
  PortfolioDto,
  SectionAssetCreateDto
} from '@unistocks/proxy/axios-client';
import { useCurrencyList } from '@unistocks/shared/hooks/use-currency-list';
import { assetTypeHasQuantity, assetTypeHasTotalValue } from '@unistocks/shared/utils/asset-utils';
import {
  currencyTypeaheadFilter,
  currencyValueFormatter
} from '@unistocks/shared/utils/currency-utils';
import { assetTypeOptions } from '@unistocks/shared/utils/enums/asset-type-options';
import { extractSymbol } from '@unistocks/shared/utils/list-utils';
import { isDefined, isNumber } from '@unistocks/shared/utils/util';
import { FormPropsBase } from '@unistocks/types/form-props';
import { Nullable } from '@unistocks/types/util/nullable';
import React, { useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod';

export type AddAssetFormProps = FormPropsBase<AddAssetFormData> & {
  portfolio: PortfolioBase;
};

type AddAssetFormData = SectionAssetCreateDto & {
  symbol?: string;
};

const portfolioAssetTypes = assetTypeOptions
  .filter(
    ({ value }) => ![AssetType.SavingsAccount, AssetType.Index, AssetType.Commodity].includes(value)
  )
  .map(({ value }) => value);
const watchlistAssetTypes = assetTypeOptions
  .filter(
    ({ value }) => ![AssetType.Cash, AssetType.SavingsAccount, AssetType.Loan].includes(value)
  )
  .map(({ value }) => value);

const assetTypeValues = assetTypeOptions.map(x => x.value);

const schemaFactory = (isPortfolio: boolean) =>
  applyRefinements(
    z.object({
      type: createNumberSchema().refine(
        value => value !== null && assetTypeValues.includes(value),
        t('::ValidationError:Required:Selection', { 0: t('::AssetType') })
      ),
      assetSearchResult: z.record(z.string(), z.any()).nullish(),
      name: requiredSchema(z.string(), t('::Name')),
      quantity: createMoreThanSchema(0, t('::Quantity')).nullish(),
      currentPrice: createMoreThanSchema(0, t('::AssetPrice')).nullish(),
      totalValue: createMoreThanSchema(0, t('::Sum')).nullish(),
      currency: z.string().nullish(),
      averageAcquisitionPrice: createMoreThanSchema(0, t('::AverageAcquisitionPrice')).nullish(),
      symbol: z.string().nullish()
    }),
    conditionalRequired(() => !isPortfolio, 'assetSearchResult', { fieldName: t('::Search') }),
    conditionalRequired(
      (assetType: AssetType, totalValue: number) =>
        isPortfolio && assetTypeHasQuantity(assetType) && !isNumber(totalValue),
      'quantity',
      { fieldName: t('::Quantity'), dependsOn: ['type', 'totalValue'] }
    ),
    conditionalRequired(
      (symbol: string, assetType: AssetType, totalValue: number) =>
        isPortfolio &&
        !isDefined(symbol) &&
        assetTypeHasQuantity(assetType) &&
        !isNumber(totalValue),
      'currentPrice',
      { fieldName: t('::AssetPrice'), dependsOn: ['symbol', 'type', 'totalValue'] }
    ),
    conditionalRequired(
      (symbol: string, assetType: AssetType) =>
        isPortfolio && !isDefined(symbol) && assetTypeHasTotalValue(assetType),
      'totalValue',
      { fieldName: t('::Sum'), dependsOn: ['symbol', 'type'] }
    ),
    conditionalRequired(
      (assetSearchResult: ExternalAssetSearchDto) => isPortfolio && !isDefined(assetSearchResult),
      'currency',
      {
        fieldName: t('::Currency'),
        dependsOn: ['assetSearchResult', 'type'],
        message: t('::ValidationError:Required:Selection', { 0: t('::DisplayName:Currency') })
      }
    )
  );

export const AddAssetForm: React.FC<AddAssetFormProps> = ({
  portfolio,
  isSubmitting,
  onSubmit,
  onCancel
}) => {
  const isPortfolio = isPortfolioCheck(portfolio);
  const schema = schemaFactory(isPortfolio);
  const [step, setStep] = useState(0);

  const initialValues: Partial<AddAssetFormData> = {
    currency: (portfolio as PortfolioDto)?.currency
  };

  return (
    <MultiStepForm<AddAssetFormData>
      schema={schema}
      initialValues={initialValues}
      isLoading={isSubmitting}
      triggerText={t('::Add')}
      step={step}
      onStepChange={setStep}
      onSubmit={onSubmit}
      onCancel={onCancel}
    >
      <MultiStepForm.Step<AddAssetFormData> label={t('::AssetType')}>
        <SelectAssetTypeStep
          assetTypes={isPortfolio ? portfolioAssetTypes : watchlistAssetTypes}
          setStep={setStep}
        />
      </MultiStepForm.Step>

      <MultiStepForm.Step<AddAssetFormData> label={t('::Asset')}>
        <SelectAssetStep />
      </MultiStepForm.Step>

      <MultiStepForm.Step<AddAssetFormData> label={t('::Holdings')} hidden={!isPortfolio}>
        <AssetSettingsStep portfolio={portfolio} />
      </MultiStepForm.Step>
    </MultiStepForm>
  );
};

AddAssetForm.displayName = 'AddAssetForm';

type AssetTypeStepProps = {
  assetTypes: AssetType[];
  setStep: (step: number) => void;
};

const SelectAssetTypeStep: React.FC<AssetTypeStepProps> = ({ assetTypes, setStep }) => {
  const { resetField, setValue } = useFormContext<AddAssetFormData>();
  const selectedAssetType = useWatch<AddAssetFormData, 'type'>({ name: 'type' });

  const onAssetTypeChange = (type: AssetType) => {
    if (isDefined(type)) {
      resetField('assetSearchResult', {
        keepTouched: true,
        keepDirty: true
      });
    }
  };

  return (
    <Form.FieldContainer<AddAssetFormData> name="type">
      <AssetTypeSelect
        assetTypes={assetTypes}
        assetType={selectedAssetType as AssetType}
        onChange={type => {
          setValue('type', type);
          onAssetTypeChange(type);
          setStep(1);
        }}
      />
    </Form.FieldContainer>
  );
};

SelectAssetTypeStep.displayName = 'SelectAssetTypeStep';

const SelectAssetStep: React.FC = () => {
  const { setValue, resetField } = useFormContext<AddAssetFormData>();
  const selectedAssetType = useWatch<AddAssetFormData, 'type'>({ name: 'type' }) as
    AssetType | undefined;

  const assetSearchHidden = (type: AssetType) =>
    [AssetType.Cash, AssetType.SavingsAccount, AssetType.Loan].includes(type);

  const { search } = useAssetSearch(selectedAssetType ?? []);

  const onAssetSearchChange = (item?: ExternalAssetSearchDto) => {
    setValue('name', item?.name ?? '', {
      shouldDirty: true,
      shouldValidate: true,
      shouldTouch: true
    });
    setValue('symbol', item?.symbol ?? undefined);
    resetField('currency');
  };

  return (
    <>
      <Form.Typeahead
        name={'assetSearchResult'}
        label={t('::Search')}
        placeholder={t('::AssetSearch:Placeholder', {
          0: t(`::Enum:AssetType:${selectedAssetType}`).toLowerCase()
        })}
        keyExtractor={extractSymbol}
        valueFormatter={(asset: ExternalAssetSearchDto) => asset.name ?? ''}
        hidden={assetSearchHidden}
        dependsOn={'type'}
        search={search}
        helpText={t('::AssetSearch:HelpText', {
          0: t(`::Enum:AssetType:${selectedAssetType}`).toLowerCase()
        })}
        onChange={onAssetSearchChange}
        renderItemIcon={item =>
          (item.value?.imageUri ?? item.value.market) ? (
            <Image
              rounded
              size={'$1.5'}
              source={
                item.value?.imageUri ??
                (item.value.market ? FLAGS[item.value.market as FlagKey] : undefined)
              }
            />
          ) : (
            <UnistocksCircle size={'$1.5'} />
          )
        }
      />
      <Form.Input
        name={'symbol'}
        label={t('::Asset:Ticker')}
        isDisabled
        hidden={(assetSearchResult: ExternalAssetSearchDto) =>
          !isDefined(assetSearchResult?.symbol)
        }
        dependsOn={'assetSearchResult'}
      />

      <Form.Input name={'name'} label={t('::Name')} isRequired />
    </>
  );
};

SelectAssetStep.displayName = 'SelectAssetStep';

type AssetSettingsStepProps = { portfolio: PortfolioBase };

const AssetSettingsStep: React.FC<AssetSettingsStepProps> = ({ portfolio }) => {
  const quantityRef = useRef<InputRef>(null);
  const currentPrice = useRef<InputRef>(null);
  const averageAcquisitionPriceRef = useRef<InputRef>(null);
  const totalValueRef = useRef<InputRef>(null);
  const currencyRef = useRef<TypeaheadRef>(null);

  const assetSearchResult = useWatch<AddAssetFormData, 'assetSearchResult'>({
    name: 'assetSearchResult'
  }) as ExternalAssetSearchDto | undefined;

  const isPortfolio = isPortfolioCheck(portfolio);
  const isWatchlist = isWatchlistCheck(portfolio);

  const currencies = useCurrencyList();

  const quantityDisabled = (type: AssetType, totalValue: number) =>
    isWatchlist || !assetTypeHasQuantity(type) || isDefined(totalValue);

  const quantityHidden = (type: AssetType, totalValue: number) =>
    isWatchlist || !assetTypeHasQuantity(type) || isDefined(totalValue);

  const currentPriceDisabled = (type: AssetType, totalValue: number) =>
    quantityDisabled(type, totalValue);

  const currentPriceHidden = (
    type: AssetType,
    totalValue: number,
    assetSearchResult: ExternalAssetSearchDto
  ) =>
    !(
      isPortfolio &&
      !isDefined(assetSearchResult?.symbol) &&
      assetTypeHasQuantity(type) &&
      !isDefined(totalValue)
    );

  const totalValueDisabled = (type: AssetType, quantity: number, currentPrice: number) =>
    isWatchlist ||
    [AssetType.Stock, AssetType.Fund, AssetType.CryptoCurrency].includes(type) ||
    isDefined(quantity) ||
    isDefined(currentPrice);

  const totalValueHidden = (type: AssetType, quantity: number, currentPrice: number) =>
    !(
      isPortfolio &&
      assetTypeHasTotalValue(type) &&
      !isDefined(quantity) &&
      !isDefined(currentPrice)
    );

  const currencyHidden = (assetSearchResult?: Nullable<ExternalAssetSearchDto>) =>
    isWatchlist || isDefined(assetSearchResult);

  return (
    <>
      <Form.Input
        ref={quantityRef}
        name={'quantity'}
        label={t('::Quantity')}
        keyboardType={'numeric'}
        isDisabled={quantityDisabled}
        hidden={quantityHidden}
        isRequired
        dependsOn={['type', 'totalValue']}
        returnKeyType="next"
        onSubmitEditing={() => currentPrice.current?.focus()}
      />

      <Form.Input
        ref={currentPrice}
        name={'currentPrice'}
        label={t('::AssetPrice')}
        keyboardType={'numeric'}
        isDisabled={currentPriceDisabled}
        hidden={currentPriceHidden}
        isRequired
        dependsOn={['type', 'totalValue', 'assetSearchResult']}
        returnKeyType="next"
        onSubmitEditing={() => averageAcquisitionPriceRef.current?.focus()}
      />

      <Form.Input
        ref={averageAcquisitionPriceRef}
        name={'averageAcquisitionPrice'}
        label={t('::AverageAcquisitionPrice')}
        keyboardType={'numeric'}
        appendText={(portfolio as PortfolioDto).currency}
        isDisabled={quantityDisabled}
        hidden={quantityHidden}
        dependsOn={['type', 'totalValue']}
        returnKeyType={!currencyHidden(assetSearchResult) ? 'next' : 'send'}
        onSubmitEditing={() => currencyRef.current?.focus?.()}
      />

      <Form.Input
        ref={totalValueRef}
        name={'totalValue'}
        label={t('::Sum')}
        keyboardType={'numeric'}
        isDisabled={totalValueDisabled}
        hidden={totalValueHidden}
        dependsOn={['type', 'quantity', 'currentPrice']}
        returnKeyType={!currencyHidden(assetSearchResult) ? 'next' : 'send'}
        onSubmitEditing={() => currencyRef.current?.focus?.()}
      />

      <Form.Typeahead
        ref={currencyRef}
        name={'currency'}
        label={t('::DisplayName:Currency')}
        placeholder={t('::DisplayName:Currency:Placeholder')}
        filter={currencyTypeaheadFilter}
        valueFormatter={currencyValueFormatter}
        options={currencies}
        hidden={currencyHidden}
        isRequired
        dependsOn={'assetSearchResult'}
      />
    </>
  );
};

AssetSettingsStep.displayName = 'AssetSettingsStep';
